import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import { marketerService, ApiError } from '../services';

const MarketerDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const marketer = location.state?.marketer;
  const [detailMarketer, setDetailMarketer] = useState(marketer || null);

  // State for downline data
  const [downline, setDownline] = useState([]);
  const [downlineLoading, setDownlineLoading] = useState(false);
  const [downlineError, setDownlineError] = useState(null);
  const [downlineActiveTab, setDownlineActiveTab] = useState('marketer');
  const [downlinePagination, setDownlinePagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });
  const lastFetchedIdRef = useRef(null);

  // Keep local marketer details in sync with route changes
  useEffect(() => {
    setDetailMarketer(marketer || null);
  }, [id, marketer]);

  // Redirect to marketers list if no marketer data is provided
  useEffect(() => {
    if (!detailMarketer) {
      navigate(ROUTES.MARKETERS, { replace: true });
    }
  }, [detailMarketer, navigate]);

  // Fetch marketer downline
  const fetchMarketerDownline = useCallback(
    async (page = 1, tabOverride = null) => {
      if (!detailMarketer?.accountId) return;

      try {
        setDownlineLoading(true);
        setDownlineError(null);

        const activeTab = tabOverride || downlineActiveTab;
        let response;
        if (activeTab === 'shopper') {
          response = await marketerService.getFormattedShopperDownline(
            detailMarketer.accountId,
            { page, size: downlinePagination.size, order: 'created_at DESC' }
          );
        } else if (activeTab === 'partner') {
          response = await marketerService.getFormattedPartnerDownline(
            detailMarketer.accountId,
            { page, size: downlinePagination.size, order: 'created_at DESC' }
          );
        } else {
          response = await marketerService.getFormattedMarketerDownline(
            detailMarketer.accountId,
            { page, size: downlinePagination.size, order: 'created_at DESC' }
          );
        }

        if (response && response.data) {
          setDownline(response.data.data || []);
          setDownlinePagination({
            page: response.data.page || 1,
            pageCount: response.data.pageCount || 1,
            size: response.data.size || 20,
            count: response.data.count || 0,
            total: response.data.total || 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch marketer downline:', err);
        if (err instanceof ApiError) {
          setDownlineError(`Failed to load downline: ${err.message}`);
        } else {
          setDownlineError('Failed to load downline. Please try again.');
        }
        setDownline([]);
      } finally {
        setDownlineLoading(false);
      }
    },
    [detailMarketer?.accountId, downlinePagination.size, downlineActiveTab]
  );

  // Handle downline pagination
  const handleDownlinePageChange = useCallback(
    newPage => {
      fetchMarketerDownline(newPage);
    },
    [fetchMarketerDownline]
  );

  // Fetch downline when marketer ID changes (once per marketer)
  useEffect(() => {
    if (!detailMarketer?.accountId) return;
    if (lastFetchedIdRef.current === detailMarketer.accountId) return;
    lastFetchedIdRef.current = detailMarketer.accountId;

    // Reset state for the new marketer's downline
    setDownline([]);
    setDownlinePagination(prev => ({
      ...prev,
      page: 1,
      pageCount: 1,
      count: 0,
      total: 0,
    }));
    fetchMarketerDownline(1); // Fetch the first page of the new downline
  }, [id, detailMarketer?.accountId]);

  // Handle downline tab change
  const handleDownlineTabChange = useCallback(
    newTab => {
      if (newTab === downlineActiveTab) return;
      setDownlineActiveTab(newTab);
      setDownline([]);
      setDownlinePagination(prev => ({
        ...prev,
        page: 1,
        pageCount: 1,
        count: 0,
        total: 0,
      }));
      // Fetch immediately with selected tab
      fetchMarketerDownline(1, newTab);
    },
    [downlineActiveTab, fetchMarketerDownline]
  );

  // Removed the effect that fetched on active tab change to avoid duplicate initial calls

  // Add breadcrumbs for navigation context
  const breadcrumbs = [
    { name: 'Home', href: ROUTES.DASHBOARD },
    { name: 'Creators', href: ROUTES.MARKETERS },
    { name: detailMarketer?.firstName || 'Detail' },
  ];

  // If no marketer data, show loading or redirect
  if (!detailMarketer) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <div className='flex items-center space-x-2 text-center'>
          <div>
            <svg
              className='animate-spin h-8 w-8 text-blue-600 mx-auto mb-2'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
            <p className='text-gray-600'>No marketer data found</p>
            <p className='text-sm text-gray-500'>
              Redirecting to marketers list...
            </p>
            <button
              onClick={() => navigate(ROUTES.MARKETERS)}
              className='mt-2 text-blue-600 hover:text-blue-800 underline'
            >
              Go to Creators List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Creator Detail'
        subtitle='View detailed information about this marketer and their downline'
        breadcrumbs={breadcrumbs}
      />

      {/* Creator Details Section  */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-medium text-gray-900'>CREATOR DETAIL</h3>
        </div>

        <div className='p-6 space-y-8'>
          {/* Personal Information */}
          <div>
            <h4 className='text-base font-semibold text-gray-800 border-b pb-2 mb-4'>
              Personal Information
            </h4>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6'>
              {/* Column 1 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    First Name
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.firstName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Last Name
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.lastName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Birthdate
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.dateOfBirth
                      ? moment(detailMarketer.dateOfBirth).format(
                          'MMM, DD YYYY'
                        )
                      : '-'}
                  </p>
                </div>
              </div>

              {/* Column 2 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Phone Number
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>+1234567890</p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Email Address
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    creator@example.com
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Join/Registered Datetime
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.joinDate
                      ? moment(detailMarketer.joinDate).format('MMM, DD YYYY')
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Account & Subscription */}
          <div>
            <h4 className='text-base font-semibold text-gray-800 border-b pb-2 mb-4'>
              Account & Subscription
            </h4>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6'>
              {/* Column 1 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Account Type
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>Plus</p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Subscription Date
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.subscriptionDate
                      ? moment(detailMarketer.subscriptionDate).format(
                          'MMM, DD YYYY'
                        )
                      : '-'}
                  </p>
                </div>
              </div>

              {/* Column 2 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Subscription Due Date
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.subscriptionDueDate
                      ? moment(detailMarketer.subscriptionDueDate).format(
                          'MMM, DD YYYY'
                        )
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Account Status
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.isActive || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Earnings & Bonuses */}
          <div>
            <h4 className='text-base font-semibold text-gray-800 border-b pb-2 mb-4'>
              Earnings & Bonuses
            </h4>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6'>
              {/* Column 1 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Bonus Level
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>Gold</p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Volume Point (VP)
                  </label>
                  <p className='text-sm text-gray-700 mt-1 font-semibold'>
                    {downlineActiveTab === 'partner' ||
                    downlineActiveTab === 'shopper'
                      ? 'N/A'
                      : Number.isFinite(detailMarketer?.careerPoint)
                      ? detailMarketer.careerPoint
                      : 0}
                  </p>
                </div>
              </div>

              {/* Column 2 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Creator Bonus
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    EUR{' '}
                    {Number.isFinite(detailMarketer?.careerBonus)
                      ? detailMarketer.careerBonus
                      : 0}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Volume Bonus & Commission
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>EUR 500.00</p>
                </div>
              </div>
            </div>
          </div>

          {/* Banking / Payout */}
          <div>
            <h4 className='text-base font-semibold text-gray-800 border-b pb-2 mb-4'>
              Banking / Payout
            </h4>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6'>
              <div>
                <label className='text-sm font-medium text-gray-900'>
                  Bank Account Information
                </label>
                <p className='text-sm text-gray-700 mt-1'>
                  Bank Name - **** 1234
                </p>
              </div>
            </div>
          </div>

          {/* Existing Upline/Downline Info - can be integrated or kept separate */}
          <div className='pt-6 border-t'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6'>
              {/* Column 1 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Upline
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.upline || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Direct Partner
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.partner || '0'}
                  </p>
                </div>
              </div>
              {/* Column 2 */}
              <div className='space-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Total Downline
                  </label>
                  <p className='text-sm text-gray-700 mt-1 font-semibold'>
                    {detailMarketer?.downline || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Downline Section  */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <div className='flex items-center justify-between'>
            <h3 className='text-lg font-medium text-gray-900 flex items-center gap-2'>
              Downline
              {downlineLoading && (
                <svg
                  className='animate-spin h-4 w-4 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h3>
            {downlinePagination.count > 0 && (
              <p className='text-sm text-gray-500'>
                Showing{' '}
                {(downlinePagination.page - 1) * downlinePagination.size + 1} to{' '}
                {Math.min(
                  downlinePagination.page * downlinePagination.size,
                  downlinePagination.count
                )}{' '}
                of {downlinePagination.count} downline members
              </p>
            )}
          </div>

          {/* Tabs */}
          <div className='mt-4 border-b border-gray-200'>
            <nav
              className='flex -mb-px space-x-6'
              role='tablist'
              aria-label='Downline tabs'
            >
              <button
                type='button'
                onClick={() => handleDownlineTabChange('marketer')}
                className={`${
                  downlineActiveTab === 'marketer'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium`}
              >
                Marketer
              </button>
              <button
                type='button'
                onClick={() => handleDownlineTabChange('partner')}
                className={`${
                  downlineActiveTab === 'partner'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium`}
              >
                Partner
              </button>
              <button
                type='button'
                onClick={() => handleDownlineTabChange('shopper')}
                className={`${
                  downlineActiveTab === 'shopper'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium`}
              >
                Shopper
              </button>
            </nav>
          </div>
        </div>

        {/* Error Display */}
        {downlineError && (
          <div className='px-6 py-4 border-b border-gray-200'>
            <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
              <div className='flex items-center'>
                <svg
                  className='w-5 h-5 text-red-400 mr-2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
                <p className='text-sm text-red-700'>{downlineError}</p>
                <button
                  onClick={() => setDownlineError(null)}
                  className='ml-auto text-red-400 hover:text-red-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32'>
                  ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider '>
                  Last Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  <>
                    Status
                    <br />
                    (Free/Plus)
                  </>
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider '>
                  Join Date
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider '>
                  Referral Code
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider '>
                  Direct Partner
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Downline
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {downline.length === 0 && !downlineLoading ? (
                <tr>
                  <td
                    colSpan='9'
                    className='px-6 py-8 text-center text-gray-500'
                  >
                    <div className='flex flex-col items-center'>
                      <svg
                        className='w-12 h-12 text-gray-300 mb-3'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                        />
                      </svg>
                      <p className='text-lg font-medium'>
                        No downline members found
                      </p>
                      <p className='text-sm'>
                        This marketer doesn't have any downline members yet.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                downline.map((member, index) => (
                  <tr key={index} className='hover:bg-gray-50'>
                    <td className='hidden lg:table-cell px-3 py-4 text-xs text-gray-900 font-mono'>
                      <div
                        className='max-w-[150px] break-all'
                        title={member.id}
                      >
                        {member.id}
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-gray-900'>
                        {member.firstName}
                      </div>
                      <div className='md:hidden text-xs text-gray-500 truncate max-w-12'>
                        {member.lastName}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 '>
                      {member.lastName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          member.status === 'Plus'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.status}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 '>
                      {member.joinDate}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-xs text-gray-900 font-mono '>
                      <div className='' title={member.referralCode}>
                        {member.referralCode}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold '>
                      {member.partner || 0}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                      {member.downline || 0}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center'>
                      <button
                        onClick={() =>
                          navigate(`/marketers/${member.id}`, {
                            state: { marketer: member },
                          })
                        }
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Downline Pagination */}
        {downlinePagination.pageCount > 1 && (
          <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
            <div className='flex-1 flex justify-between sm:hidden'>
              <button
                onClick={() =>
                  handleDownlinePageChange(downlinePagination.page - 1)
                }
                disabled={downlinePagination.page <= 1 || downlineLoading}
                className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Previous
              </button>
              <button
                onClick={() =>
                  handleDownlinePageChange(downlinePagination.page + 1)
                }
                disabled={
                  downlinePagination.page >= downlinePagination.pageCount ||
                  downlineLoading
                }
                className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next
              </button>
            </div>
            <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
              <div>
                <p className='text-sm text-gray-700'>
                  Page{' '}
                  <span className='font-medium'>{downlinePagination.page}</span>{' '}
                  of{' '}
                  <span className='font-medium'>
                    {downlinePagination.pageCount}
                  </span>
                </p>
              </div>
              <div>
                <nav
                  className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                  aria-label='Pagination'
                >
                  <button
                    onClick={() =>
                      handleDownlinePageChange(downlinePagination.page - 1)
                    }
                    disabled={downlinePagination.page <= 1 || downlineLoading}
                    className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Previous</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from(
                    { length: Math.min(5, downlinePagination.pageCount) },
                    (_, i) => {
                      let pageNum;
                      if (downlinePagination.pageCount <= 5) {
                        pageNum = i + 1;
                      } else if (downlinePagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (
                        downlinePagination.page >=
                        downlinePagination.pageCount - 2
                      ) {
                        pageNum = downlinePagination.pageCount - 4 + i;
                      } else {
                        pageNum = downlinePagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handleDownlinePageChange(pageNum)}
                          disabled={downlineLoading}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === downlinePagination.page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() =>
                      handleDownlinePageChange(downlinePagination.page + 1)
                    }
                    disabled={
                      downlinePagination.page >= downlinePagination.pageCount ||
                      downlineLoading
                    }
                    className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Next</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketerDetailPage;
